import type { FC, ReactNode } from 'react'
import type { CategoriaResumen } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { ArrowRight, ChevronDown, Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import { buttonVariants } from '@/components/ui/button'
import Container from '@/components/generals/container'
import Logo from '@/components/generals/logo'

import {
    Sheet,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet'

import { mainNavigation, quoteHref } from '@/constants/site'

interface SiteHeaderProps {
    categorias: Array<CategoriaResumen>
}

const isActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'
    if (href === '/catalogo') return pathname.startsWith('/catalogo') || pathname.startsWith('/productos')

    return pathname === href || pathname.startsWith(`${href}/`)
}

const navLinkClass = (active: boolean): string => cn(
    'relative inline-flex items-center gap-1.5 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.22em] transition-colors hover:text-brass',
    'after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left after:bg-brass after:transition-transform after:duration-300',
    active ? 'text-brass after:scale-x-100' : 'text-ivory/80 after:scale-x-0 hover:after:scale-x-100'
)

const SiteHeader: FC<SiteHeaderProps> = ({ categorias }): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const closeMenu = () => setOpen(false)

    return (
        <header
            className={cn(
                'sticky top-0 z-40 border-b transition-[background-color,border-color] duration-300',
                scrolled ? 'border-ivory/10 bg-black/90 supports-backdrop-filter:bg-black/75 supports-backdrop-filter:backdrop-blur-md' : 'border-transparent bg-black'
            )}
        >
            <Container className='flex h-20 items-center justify-between gap-6'>
                <Link href='/' aria-label='TROFÉA, ir al inicio' className='shrink-0'>
                    <Logo />
                </Link>

                <nav aria-label='Navegación principal' className='hidden lg:block'>
                    <ul className='flex items-center gap-8 xl:gap-10'>
                        {mainNavigation.map(item => {
                            const active = isActive(router.pathname, item.href)

                            if (item.href === '/catalogo') {
                                return (
                                    <li key={item.href} className='group relative'>
                                        <Link href={item.href} aria-current={active ? 'page' : undefined} className={navLinkClass(active)}>
                                            {item.label}
                                            <ChevronDown aria-hidden='true' className='size-3.5 transition-transform duration-300 group-hover:rotate-180 group-focus-within:rotate-180' />
                                        </Link>

                                        <div className='invisible absolute top-full left-1/2 w-72 -translate-x-1/2 pt-5 opacity-0 transition duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100'>
                                            <ul className='rounded-sm border border-ivory/10 bg-black/95 p-2 shadow-2xl shadow-black/60'>
                                                {categorias.map(categoria => (
                                                    <li key={categoria.slug}>
                                                        <Link
                                                            href={`/catalogo/${categoria.slug}`}
                                                            className='flex items-center justify-between rounded-sm px-4 py-3 text-sm text-ivory/85 transition-colors hover:bg-graphite hover:text-brass'
                                                        >
                                                            <span data-mdlfy-cms={cms('categorias', categoria.id, 'nombre')}>{categoria.nombre}</span>
                                                            <ArrowRight aria-hidden='true' className='size-3.5 text-brass/70' />
                                                        </Link>
                                                    </li>
                                                ))}

                                                <li className='mt-2 border-t border-ivory/10 pt-2'>
                                                    <Link
                                                        href='/catalogo'
                                                        className='block rounded-sm px-4 py-3 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-brass transition-colors hover:bg-graphite'
                                                    >
                                                        Ver catálogo completo
                                                    </Link>
                                                </li>
                                            </ul>
                                        </div>
                                    </li>
                                )
                            }

                            return (
                                <li key={item.href}>
                                    <Link href={item.href} aria-current={active ? 'page' : undefined} className={navLinkClass(active)}>
                                        {item.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex items-center gap-3'>
                    <Link href={quoteHref} className={cn(buttonVariants({ size: 'sm' }), 'hidden sm:inline-flex')}>
                        Solicitar cotización
                    </Link>

                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger
                            aria-label='Abrir menú'
                            className='inline-flex size-11 items-center justify-center rounded-sm border border-ivory/15 text-foreground transition-colors hover:border-brass hover:text-brass lg:hidden'
                        >
                            <Menu aria-hidden='true' className='size-5' />
                        </SheetTrigger>

                        <SheetContent side='right' className='overflow-y-auto'>
                            <SheetHeader className='border-b border-ivory/10 pr-20'>
                                <SheetTitle className='sr-only'>Menú principal</SheetTitle>

                                <Link href='/' onClick={closeMenu} aria-label='TROFÉA, ir al inicio' className='w-fit'>
                                    <Logo />
                                </Link>
                            </SheetHeader>

                            <nav aria-label='Menú móvil' className='px-6'>
                                <ul className='flex flex-col'>
                                    {mainNavigation.map(item => {
                                        const active = isActive(router.pathname, item.href)

                                        return (
                                            <li key={item.href} className='border-b border-ivory/10 py-4'>
                                                <Link
                                                    href={item.href}
                                                    onClick={closeMenu}
                                                    aria-current={active ? 'page' : undefined}
                                                    className={cn('font-heading text-3xl uppercase tracking-wide transition-colors hover:text-brass', active ? 'text-brass' : 'text-foreground')}
                                                >
                                                    {item.label}
                                                </Link>

                                                {item.href === '/catalogo' && categorias.length > 0 && (
                                                    <ul className='mt-3 grid grid-cols-2 gap-x-4 gap-y-2'>
                                                        {categorias.map(categoria => (
                                                            <li key={categoria.slug}>
                                                                <Link
                                                                    href={`/catalogo/${categoria.slug}`}
                                                                    onClick={closeMenu}
                                                                    className='text-sm text-muted-foreground transition-colors hover:text-brass'
                                                                >
                                                                    <span data-mdlfy-cms={cms('categorias', categoria.id, 'nombre')}>{categoria.nombre}</span>
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </li>
                                        )
                                    })}
                                </ul>
                            </nav>

                            <SheetFooter className='border-t border-ivory/10'>
                                <Link href={quoteHref} onClick={closeMenu} className={buttonVariants({ size: 'lg' })}>
                                    Solicitar cotización
                                    <ArrowRight aria-hidden='true' />
                                </Link>
                            </SheetFooter>
                        </SheetContent>
                    </Sheet>
                </div>
            </Container>
        </header>
    )
}

export default SiteHeader
